import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Motif } from "../components/Motif";
import ProductCard from "../components/ProductCard";
import { fetchProducts } from "../lib/api";
import SaffronRecipes from "../components/SaffronRecipes";

const SAFFRON_HERO = "/brand/saffron-hero.jpg";
const SAFFRON_FLOWER = "/brand/saffron-harvest.jpg";
const SAFFRON_HANDS = "/brand/saffron-trio.jpg";

const MarigoldIcon = ({ size = 22, color = "currentColor" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="1.2" aria-hidden="true">
    <circle cx="12" cy="12" r="2.4" />
    <path d="M12 2.4c1.4 1.8 1.4 4.8 0 7.2C10.6 7.2 10.6 4.2 12 2.4Z" />
    <path d="M12 21.6c-1.4-1.8-1.4-4.8 0-7.2 1.4 2.4 1.4 5.4 0 7.2Z" />
    <path d="M2.4 12c1.8-1.4 4.8-1.4 7.2 0-2.4 1.4-5.4 1.4-7.2 0Z" />
    <path d="M21.6 12c-1.8 1.4-4.8 1.4-7.2 0 2.4-1.4 5.4-1.4 7.2 0Z" />
    <path d="M5 5c2 .6 4 2.4 4.8 4.8C7.4 9 5.6 7 5 5Z" />
    <path d="M19 19c-2-.6-4-2.4-4.8-4.8 2.4.8 4.2 2.8 4.8 4.8Z" />
    <path d="M19 5c-.6 2-2.6 4-4.8 4.8C15 7.4 17 5.6 19 5Z" />
    <path d="M5 19c.6-2 2.6-4 4.8-4.8C9 16.6 7 18.4 5 19Z" />
  </svg>
);

const Saffron = () => {
  const { t, lang } = useLang();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts("saffron").then(setProducts).catch(() => setProducts([]));
  }, []);

  return (
    <div data-testid="page-saffron">
      {/* HERO — saffron-toned full bleed */}
      <section
        className="relative pt-24 md:pt-28 pb-20 md:pb-32 overflow-hidden bg-[#FAF9F6]"
        data-testid="saffron-hero"
      >
        <div className="absolute inset-0">
          <img
            src={SAFFRON_HERO}
            alt=""
            className="w-full h-full object-cover"
            style={{ mixBlendMode: "multiply" }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#FAF9F6]/30 via-[#FAF9F6]/10 to-[#1E2B3C]/40" />
        </div>

        <div className="relative px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-10 items-end pt-14 md:pt-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-8 text-[#1E2B3C]"
          >
            <div className="flex items-center gap-3 mb-6">
              <MarigoldIcon size={26} color="#B34A36" />
              <p className="overline" style={{ letterSpacing: "0.22em" }}>
                {t.saffron.overline}
              </p>
            </div>
            <h1 className="font-serif-display font-light text-[3rem] sm:text-6xl lg:text-[7rem] leading-[0.95] tracking-tight">
              {t.saffron.title}
              <br />
              <span className="italic text-[#B34A36]">{t.saffron.titleAccent}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-[#1A1A1A]/80">
              {t.saffron.lede}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="lg:col-span-4 flex lg:justify-end"
          >
            <div className="bg-[#1E2B3C] text-[#FAF9F6] p-8 md:p-10 max-w-sm border-l-2 border-[#D99A29]">
              <p className="overline mb-3" style={{ color: "#D99A29" }}>
                {lang === "fr" ? "IGP Kashmir Saffron" : "Kashmir Saffron GI"}
              </p>
              <p className="font-serif-display text-2xl md:text-3xl leading-[1.15] font-light">
                {lang === "fr"
                  ? "« Le seul safran reconnu au monde pour sa concentration supérieure à 45 en safranal. »"
                  : '"The only saffron in the world recognised for its safranal content above 45."'}
              </p>
              <p className="mt-5 text-xs tracking-[0.18em] uppercase text-white/60">
                {lang === "fr" ? "Récolte 2025 · Pampore" : "2025 Harvest · Pampore"}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* IMAGE STRIP */}
      <section className="grid md:grid-cols-2 gap-1 bg-[#FAF9F6]" data-testid="saffron-images">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="aspect-[5/4] md:aspect-[4/3] overflow-hidden grain"
        >
          <img src={SAFFRON_FLOWER} alt="Crocus sativus" className="w-full h-full object-cover" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="aspect-[5/4] md:aspect-[4/3] overflow-hidden grain bg-[#1E2B3C]"
        >
          <img src={SAFFRON_HANDS} alt="Hand-sorting" className="w-full h-full object-cover" />
        </motion.div>
      </section>

      {/* PILLARS */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#FAF9F6]" data-testid="saffron-pillars">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <p className="overline mb-4">{lang === "fr" ? "Méthode" : "Method"}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl font-light leading-[1.05] text-[#1E2B3C]">
              {lang === "fr"
                ? "Quatre gestes, du champ au flacon."
                : "Four gestures, from field to vial."}
            </h2>
          </div>
          <p className="lg:col-span-6 lg:col-start-7 text-[#5C5C5C] leading-relaxed text-lg">
            {t.saffron.ledeShort}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-14 border-t border-[#E5E1D8] pt-12">
          {t.saffron.pillars.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`saffron-pillar-${i + 1}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <MarigoldIcon size={24} color="#B34A36" />
                <p className="font-serif-display italic text-2xl text-[#B34A36]">{p.n}</p>
              </div>
              <h3 className="font-serif-display text-2xl text-[#1E2B3C] leading-tight">{p.title}</h3>
              <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32 bg-[#FAF9F6]" data-testid="saffron-products">
        <div className="mb-12">
          <p className="overline mb-3">{lang === "fr" ? "Trois qualités" : "Three grades"}</p>
          <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1E2B3C]">
            {lang === "fr" ? "Choisir son safran." : "Choosing your saffron."}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-12 md:gap-x-10">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* RECIPES — Trois recettes françaises au safran */}
      <SaffronRecipes />


      {/* RITUAL */}
      <section className="relative py-24 md:py-32 bg-[#1E2B3C] text-[#FAF9F6] overflow-hidden" data-testid="saffron-ritual">
        <Motif name="lotus" size={520} opacity={0.05} className="absolute -right-24 -bottom-32 hidden md:block" />
        <div className="px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-12 relative">
          <div className="lg:col-span-5">
            <MarigoldIcon size={48} color="#D99A29" />
            <p className="overline mt-6" style={{ color: "#D99A29" }}>
              {lang === "fr" ? "Le geste" : "The gesture"}
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] mt-4">
              {t.saffron.ritual.title}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-end">
            <p className="text-lg leading-[1.85] text-white/85 max-w-xl">{t.saffron.ritual.body}</p>
            <Link to="#saffron-products" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid=saffron-products]')?.scrollIntoView({ behavior: 'smooth' }); }} className="btn-primary mt-10 self-start bg-[#D99A29] text-[#1E2B3C] hover:bg-white" data-testid="saffron-ritual-cta">
              {t.saffron.ctaShop}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Saffron;
